/**
 * 企业大脑 — 路由认证守卫
 * 未登录时跳转到登录页，用户可选择账号登录或游客模式
 */
import { type ReactNode, useEffect } from 'react';
import { useLocation, useParams, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { usePermissionStore } from '@/stores/permissionStore';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const tenantId = useAuthStore((s) => s.tenantId);
  const { fetchPermissions, hasRouteAccess, permissions, loading, error, clearPermissions } = usePermissionStore();
  const location = useLocation();
  const params = useParams<{ tenantId?: string }>();

  const urlTenant = params.tenantId || '';
  const userTenant = tenantId || 'default';

  // 监听 auth:logout 事件
  useEffect(() => {
    const handleLogout = () => {
      logout();
      clearPermissions();
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [logout, clearPermissions]);

  // 登录后获取权限
  useEffect(() => {
    if (isAuthenticated && !permissions && !loading) {
      fetchPermissions();
    }
  }, [isAuthenticated, permissions, loading, fetchPermissions]);

  // 未登录 → 跳转到登录页
  if (!isAuthenticated) {
    const loginPath = urlTenant ? `/${urlTenant}/login` : '/login';
    return <Navigate to={loginPath} replace />;
  }

  // 检查租户路径匹配
  if (userTenant !== 'default' && urlTenant !== userTenant) {
    const pathWithoutTenant = urlTenant 
      ? location.pathname.replace(new RegExp(`^/${urlTenant}`), '')
      : location.pathname;
    const targetPath = pathWithoutTenant || '/apps';
    window.location.href = `/${userTenant}${targetPath}`;
    return null;
  } else if (userTenant === 'default' && urlTenant) {
    const pathWithoutTenant = location.pathname.replace(new RegExp(`^/${urlTenant}`), '') || '/apps';
    window.location.href = pathWithoutTenant;
    return null;
  }

  // 有缓存权限直接渲染
  if (permissions) {
    const pathForCheck = urlTenant 
      ? location.pathname.replace(new RegExp(`^/${urlTenant}`), '') || '/apps'
      : location.pathname;
    
    if (!hasRouteAccess(pathForCheck)) {
      const defaultPath = urlTenant ? `/${urlTenant}/apps` : '/apps';
      return <Navigate to={defaultPath} replace />;
    }
    return <>{children}</>;
  }

  // 权限加载中
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'var(--bg, #f5f5f5)'
      }}>
        <div style={{ textAlign: 'center', color: 'var(--text-2, #666)' }}>
          <div>加载中...</div>
        </div>
      </div>
    );
  }

  // 权限加载失败
  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: 'var(--bg, #f5f5f5)'
      }}>
        <div style={{ textAlign: 'center', color: 'var(--text-2, #666)' }}>
          <div style={{ marginBottom: '12px', color: 'var(--red, #e53935)' }}>
            {error}
          </div>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              background: 'var(--accent, #1976d2)',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            刷新重试
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
